import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { investmentsInfoService } from '../../../../services/investmentsInfo';
import { Alert, ActivityIndicator } from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { vaultService } from '../../../../services/vaultService';


import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('InvestmentsInfoInput');

const ACCOUNT_TYPES = [
  'Individual Brokerage Account',
  'Joint Brokerage Account',
  'Retirement Account (401k, 403b, etc.)',
  'Traditional IRA',
  'Roth IRA',
  'SEP IRA',
  'SIMPLE IRA',
  'Trust Account',
  'Custodial Account (UGMA/UTMA)',
  'College Savings (529 Plan, ESA)',
  'Annuity Account',
  'HSA (Health Savings Account)',
  'Pension Plan',
  'Profit-Sharing Plan',
  'Other'
];

const INVESTMENT_TYPES = [
  'Stocks',
  'Bonds',
  'Mutual Funds',
  'Exchange-Traded Funds (ETFs)',
  'Index Funds',
  'Certificates of Deposit (CDs)',
  'Money Market Funds',
  'Real Estate Investment Trusts (REITs)',
  'Options',
  'Futures',
  'Commodities (e.g., gold, oil)',
  'Private Equity',
  'Hedge Funds',
  'Cryptocurrencies',
  'Cash / Cash Equivalents',
  'Other'
];

export const InvestmentsInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { investmentId?: string; isEdit?: boolean }>, string>>();
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showInvestmentTypeModal, setShowInvestmentTypeModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedInvestmentId, setSavedInvestmentId] = useState<string | null>(null);
  const investmentId = (route.params as any)?.investmentId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Helper: format currency as $x,xxx,xxx while typing
  const formatCurrency = (text: string) => {
    const cleaned = text.replace(/[^0-9]/g, '');
    if (!cleaned) return '';
    const number = parseInt(cleaned, 10);
    return '$' + number.toLocaleString('en-US');
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };


  const [formData, setFormData] = useState({
    institutionName: '',
    accountNumber: '',
    accountType: '',
    supportPhone: '',
    supportEmail: '',
    website: '',
    username: '',
    password: '',
    accountValue: '',
    investmentType: '',
    beneficiary: '',
    notes: '',
  });

  useEffect(() => {
    if (isEdit && investmentId) {
      (async () => {
        try {
          // Load existing investment record
          const list = await investmentsInfoService.getAllInvestmentsInfo();
          const record = list.find((a: any) => a.id === investmentId);
          if (record) {
            setFormData(prev => ({
              ...prev,
              institutionName: record.financialInstitution || '',
              accountType: record.accountType || '',
              supportPhone: formatPhoneNumber(record.customerServicePhone || ''),
              supportEmail: record.supportEmail || '',
              website: record.onlineAccountUrl || '',
              accountValue: record.accountValue ? formatCurrency(record.accountValue.toString()) : '',
              investmentType: record.investmentType || '',
              beneficiary: record.beneficiaryInfo || '',
              notes: record.notes || '',
            }));

            // Try to load portal credentials (with automatic migration)
            try {
              const creds = await vaultService.getPortalCredentials(investmentId, 'investments');
              if (creds && (creds.username || creds.password || creds.accountNumber)) {
                setHasOnlinePortal(true);
                setFormData(prev => ({
                  ...prev,
                  username: creds.username || '',
                  password: creds.password || '',
                  accountNumber: creds.accountNumber || '',
                }));
              }
            } catch {}
          }
        } catch (e) {
          logger.debug('Failed to load investment for edit', e);
        }
      })();
    }
  }, [isEdit, investmentId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      const payload: any = {
        financialInstitution: formData.institutionName || undefined,
        accountType: formData.accountType || undefined,
        customerServicePhone: formData.supportPhone.replace(/\D/g, ''),
        supportEmail: formData.supportEmail || undefined,
        onlineAccountUrl: formData.website || undefined,
        accountValue: formData.accountValue ? parseFloat(formData.accountValue.replace(/[^0-9]/g, '')) : undefined,
        investmentType: formData.investmentType || undefined,
        beneficiaryInfo: formData.beneficiary || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update investment record
      let savedId = investmentId as string | undefined;
      if (isEdit && investmentId) {
        const updated = await investmentsInfoService.updateInvestmentsInfo(investmentId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
      } else {
        const saved = await investmentsInfoService.createInvestmentsInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedInvestmentId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for investment ID:', savedId);
          logger.debug('[Portal Debug] Username provided:', !!formData.username);
          logger.debug('[Portal Debug] Password provided:', !!formData.password);
          logger.debug('[Portal Debug] Account number provided:', !!formData.accountNumber);
          await vaultService.storePortalCredentials(savedId, 'investments', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.accountNumber,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for investment portal credentials:', e);
        }
      } else {
        logger.debug('[Portal Debug] No savedId or no credentials provided, skipping vault storage', { 
          savedId, 
          hasUsername: !!formData.username, 
          hasPassword: !!formData.password,
          hasAccountNumber: !!formData.accountNumber 
        });
      }

      setShowAddAnotherDone(true);
    } catch (error: any) {
      logger.error('Error saving investment info:', error);
      Alert.alert('Error', 'Failed to save investment information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Reset form and hide buttons
    setFormData({
      institutionName: '',
      accountNumber: '',
      accountType: '',
      supportPhone: '',
      supportEmail: '',
      website: '',
      username: '',
      password: '',
      accountValue: '',
      investmentType: '',
      beneficiary: '',
      notes: '',
    });
    setHasOnlinePortal(false);
    setShowUsername(false);
    setShowPassword(false);
    setShowAccountNumber(false);
    setShowAddAnotherDone(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedInvestmentId || investmentId;
    logger.debug('[Done Debug] Using ID:', idToUse, 'savedInvestmentId:', savedInvestmentId, 'investmentId:', investmentId);
    (navigation as any).navigate('InvestmentsInfoLockUnlock', { investmentsId: idToUse });
  };

  const handleAccountTypeSelect = (accountType: string) => {
    setFormData({ ...formData, accountType });
    setShowAccountTypeModal(false);
  };

  const handleInvestmentTypeSelect = (investmentType: string) => {
    setFormData({ ...formData, investmentType });
    setShowInvestmentTypeModal(false);
  };


  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit' : 'Add'} Investment Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Institution Name</Text>
            <TextInput
              style={styles.input}
              value={formData.institutionName}
              onChangeText={(text) => setFormData({ ...formData, institutionName: text })}
              placeholder="Enter Institution Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => {
                logger.debug('Opening account type modal');
                setShowAccountTypeModal(true);
              }}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.accountType && { color: '#A9A9A9' }
              ]}>
                {formData.accountType || 'Select Account Type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Support Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.supportPhone}
              onChangeText={(text) => setFormData({ ...formData, supportPhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Support Email</Text>
            <TextInput
              style={styles.input}
              value={formData.supportEmail}
              onChangeText={(text) => setFormData({ ...formData, supportEmail: text.trim().toLowerCase() })}
              placeholder="Enter Support Email"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Enter your online portal access?</Text>
              <View style={styles.toggleWrapper}>
                <TouchableOpacity
                  style={[styles.toggleButton, hasOnlinePortal && styles.toggleButtonActive]}
                  onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
                >
                  <View style={[styles.toggleCircle, hasOnlinePortal && styles.toggleCircleActive]} />
                </TouchableOpacity>
                <Text style={styles.toggleText}>{hasOnlinePortal ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Website Address</Text>
                <TextInput
                  style={styles.input}
                  value={formData.website}
                  onChangeText={(text) => setFormData({ ...formData, website: text })}
                  placeholder="Enter Portal Website URL"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="url"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Account Number 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.accountNumber}
                    onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                    placeholder="Enter Account Number"
                    placeholderTextColor="#A9A9A9"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!showAccountNumber}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowAccountNumber(!showAccountNumber)}
                  >
                    <Image
                      source={showAccountNumber ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!showUsername}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="password"
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}


          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Value</Text>
            <TextInput
              style={styles.input}
              value={formData.accountValue}
              onChangeText={(text) => setFormData({ ...formData, accountValue: formatCurrency(text) })}
              placeholder="$0"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Investment Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => {
                logger.debug('Opening investment type modal');
                setShowInvestmentTypeModal(true);
              }}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.investmentType && { color: '#A9A9A9' }
              ]}>
                {formData.investmentType || 'Select Investment Type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Beneficiary (if applicable)</Text>
            <TextInput
              style={styles.input}
              value={formData.beneficiary}
              onChangeText={(text) => setFormData({ ...formData, beneficiary: text })}
              placeholder="Enter Beneficiary Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Submit Button or Add Another/Done */}
          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={[styles.submitButton, styles.addAnotherButton]} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.submitButton, styles.doneButton]} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.submitButton, submitting && { opacity: 0.7 }]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>


      <Modal
        visible={showAccountTypeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAccountTypeModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAccountTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Account Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAccountTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.accountTypeList}>
              {ACCOUNT_TYPES.map((accountType) => (
                <TouchableOpacity
                  key={accountType}
                  style={styles.accountTypeItem}
                  onPress={() => handleAccountTypeSelect(accountType)}
                >
                  <Text style={styles.accountTypeItemText}>{accountType}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={showInvestmentTypeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowInvestmentTypeModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowInvestmentTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Investment Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowInvestmentTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.investmentTypeList}>
              {INVESTMENT_TYPES.map((investmentType) => (
                <TouchableOpacity
                  key={investmentType}
                  style={styles.investmentTypeItem}
                  onPress={() => handleInvestmentTypeSelect(investmentType)}
                >
                  <Text style={styles.investmentTypeItemText}>{investmentType}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    gap: ms(15),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F64',
    flex: 1,
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    flex: 1,
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  accountTypeList: {
    padding: ms(16),
  },
  accountTypeItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  accountTypeItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  investmentTypeList: {
    padding: ms(16),
  },
  investmentTypeItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  investmentTypeItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    flex: 1,
  },
  toggleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  toggleButton: {
    width: s(60),
    height: s(32),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(16),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#FB923C',
  },
  toggleCircle: {
    width: s(28),
    height: s(28),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleCircleActive: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    minWidth: s(30),
  },
});